import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { type SpotShape, SpotShapeType } from "../types";
import { importedTableBounds, importedTables } from "./compileImportFurniture";
import { dynamicTabletop } from "./dynamicFurniture";
import { computeWallFloorPolygons } from "./wallFloorFill";
import { getAllWallSegments } from "./wallPathSegments";

type Point = { x: number; y: number };
export interface ImportedMeetingRoomOptions {
  markerOnTable: boolean;
  highlightRoom: boolean;
}
const inside = (p: Point, points: Point[]) => {
  let result = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i],
      b = points[j];
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      result = !result;
  }
  return result;
};
function geometry(points: Point[]) {
  let twiceArea = 0,
    x = 0,
    y = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i],
      b = points[(i + 1) % points.length],
      cross = a.x * b.y - b.x * a.y;
    twiceArea += cross;
    x += (a.x + b.x) * cross;
    y += (a.y + b.y) * cross;
  }
  return {
    area: Math.abs(twiceArea) / 2,
    center: { x: x / (3 * twiceArea), y: y / (3 * twiceArea) }
  };
}
const rotate = (p: Point, a: number): Point => ({
  x: p.x * Math.cos(a) - p.y * Math.sin(a),
  y: p.x * Math.sin(a) + p.y * Math.cos(a)
});
const dot = (s: SpotShape): Point => {
  const offset = rotate(
    { x: 70 * (s.scaleX ?? 1), y: 60 * (s.scaleY ?? 1) },
    ((s.rotation || 0) * Math.PI) / 180
  );
  return { x: s.x + offset.x, y: s.y + offset.y };
};

/** Run after desk/map calibration. Nothing is added to the zone layer: room
 * highlighting is native availability stateArea geometry on the room spot. */
export function configureImportedMeetingRooms(
  source: MapCanvasValue,
  map: MapCanvasValue,
  options: ImportedMeetingRoomOptions
) {
  const warnings: string[] = [];
  const spots = map.shapes.filter(
    (s): s is SpotShape =>
      s.type === "spot" && s.spotType === SpotShapeType.Room
  );
  if (!spots.length) return { map, warnings };
  // Imports suppress automatic floor paint, but still contain the wall network
  // needed to detect room faces. Override only this private detection copy.
  const rooms = computeWallFloorPolygons(
    getAllWallSegments(map.shapes).map((w) => ({
      ...w,
      excludeFromFloorFill: false
    }))
  )
    .map((room) => ({ ...room, ...geometry(room.points) }))
    .filter((room) => room.area > 0)
    .sort((a, b) => a.area - b.area);
  const scale = map.editingZoneWidth / source.editingZoneWidth;
  const furnitureIds = new Set(
    map.shapes
      .filter(
        (s) =>
          s.type === "furniture" ||
          s.type === "dynamic-furniture" ||
          ((s.type === "rectangle" || s.type === "circle") &&
            s.layerLabel === "table")
      )
      .map((s) => s.id)
  );
  const tables = [
    ...importedTables(source),
    ...source.shapes.flatMap((s) =>
      s.type === "dynamic-furniture" ? [dynamicTabletop(s)] : []
    )
  ]
    .filter((t) => furnitureIds.has(t.id))
    .map((t) => {
      const bounds = importedTableBounds(t);
      return {
        id: t.id,
        area: t.width * t.height * scale * scale,
        center: {
          x: ((bounds.x + bounds.right) * scale) / 2,
          y: ((bounds.y + bounds.bottom) * scale) / 2
        },
        bounds: {
          x: bounds.x * scale,
          y: bounds.y * scale,
          right: bounds.right * scale,
          bottom: bounds.bottom * scale
        }
      };
    });
  const updates = new Map<string, SpotShape>();
  for (const spot of spots) {
    const location = dot(spot);
    const room = rooms.find((r) => inside(location, r.points));
    // A shared enclosure containing multiple room markers is not an individual
    // meeting room (often only the building perimeter survived recognition).
    const enclosure =
      room && spots.filter((s) => inside(dot(s), room.points)).length === 1
        ? room
        : undefined;
    const candidates = tables
      .filter((t) =>
        enclosure
          ? inside(t.center, enclosure.points)
          : location.x >= t.bounds.x &&
            location.x <= t.bounds.right &&
            location.y >= t.bounds.y &&
            location.y <= t.bounds.bottom
      )
      .sort(
        (a, b) =>
          b.area - a.area ||
          Math.hypot(a.center.x - location.x, a.center.y - location.y) -
            Math.hypot(b.center.x - location.x, b.center.y - location.y)
      );
    const table = candidates[0];
    const center =
      table?.center ||
      (enclosure && inside(enclosure.center, enclosure.points)
        ? enclosure.center
        : location);
    const markerOnly = !!table && options.markerOnTable;
    const sx = markerOnly ? 1 : (spot.scaleX ?? 1),
      sy = markerOnly ? 1 : (spot.scaleY ?? 1);
    const rotation = markerOnly ? 0 : spot.rotation || 0,
      a = (rotation * Math.PI) / 180;
    const localDot = markerOnly ? { x: 45, y: 0 } : { x: 70 * sx, y: 60 * sy };
    const offset = rotate(localDot, a);
    const next: SpotShape = {
      ...spot,
      x: center.x - offset.x,
      y: center.y - offset.y,
      rotation,
      scaleX: sx,
      scaleY: sy,
      spotAppearance: markerOnly ? SpotShapeType.Node : undefined,
      width: markerOnly ? 100 : spot.width,
      height: markerOnly ? 100 : spot.height,
      stateArea: undefined
    };
    if (options.highlightRoom) {
      if (enclosure)
        next.stateArea = {
          kind: "polygon",
          indicatorAnchor: "spot",
          hideDot: false,
          points: enclosure.points.map((p) => {
            const local = rotate({ x: p.x - next.x, y: p.y - next.y }, -a);
            return { x: local.x / sx, y: local.y / sy };
          })
        };
      else
        warnings.push(
          `${spot.label || spot.name || "Meeting room"}: no unambiguous wall-enclosed room was found for the highlight. Draw its highlight area in the editor.`
        );
    }
    updates.set(spot.id, next);
  }
  return {
    map: { ...map, shapes: map.shapes.map((s) => updates.get(s.id) || s) },
    warnings
  };
}
